<template>
  <div>
    <commonModal
      :show="showWarningModal"
      :actionButton="true"
      :options="{
        title: 'Apologies',
        description: `The site is currently unable to update your lien holder information,
                      but a Customer Service Concierge will contact you shortly.`,
        buttonLabel: 'OK'
      }"
      @close="clickedCloseOnModal"
    ></commonModal>
    <commonModal
      :show="paymentFailedModal"
      :options="{
        title: 'Call to complete your quote',
        description:
          `The system is unable to complete your request at this time.
          Please call us at
          <a href='tel:1-` +
          $phoneNumber +
          `'>` +
          $phoneNumber +
          `</a>
          to speak to a customer service concierge.`,
        buttonLabel: 'OK'
      }"
      @close="paymentFailedModal = false"
    ></commonModal>
    <form class="has-placeholders">
      <fieldset>
        <p v-if="!showLienHolders" class="legend">{{ payHeading }}</p>
        <p v-else class="legend">{{ lienHead }}</p>
        <div class="columns is-variable is-8-widescreen">
          <div class="column is-7-desktop is-8-tablet has-columns-below">
            <div class="ack-text">
              <p v-if="!showLienHolders" class="payment-lien-desc">
                Our pay-per-mile model offers you financial flexibility,
                allowing you to pay each month based on your actual miles
                driven, or pay every six months based on your estimated mileage.
                With the six month plan, if you drive less than estimated, we
                will provide you with a credit for the unused mileage; and if
                you drive more than estimated, we will send an invoice for the
                difference.
              </p>
              <p v-if="showLienHolders" class="payment-lien-desc">
                As your vehicle is financed or leased, we need the name and
                address of your lienholder before we can issue your policy. This
                information can be found on your financing or leasing documents.
              </p>
              <p v-if="showLienHolders" class="payment-lien-desc">
                If your lienholder is not listed below, you may add it manually,
                or if you are unsure of your lienholder, please call
                <nobr
                  ><a
                    style="text-decoration:underline;color:#000"
                    href="tel:1-800-400-4128"
                    >800-400-4128</a
                  ></nobr
                >
                and a concierge can assist you.
              </p>
            </div>
          </div>
        </div>
        <div
          v-if="!showLienHolders"
          class="columns is-variable is-8-widescreen"
        >
          <div class="column is-half has-columns-above left-section">
            <div class="duration-section">
              <div class="header">
                <p>Select your billing frequency:</p>
              </div>
              <div class="box -spacing-inner-c frequency-box">
                <label
                  class="input -spacing-static-c"
                  :key="radios.duration[0].value"
                  :class="{
                    radio: true,
                    error: errors.has('monthly')
                  }"
                >
                  <input
                    class="radio__field"
                    type="radio"
                    name="monthly"
                    :value="radios.duration[0].value"
                    v-model="form.selectedPricingScheme"
                    v-validate="'required'"
                  />
                  <span class="radio__icon icon icon--radio"></span>
                  <span class="radio__label">
                    I'd like to pay
                    <b>monthly</b>
                    <br />
                    <span class="sub-label-text">
                      Start your policy today for a down payment of ${{
                        roundedDownPaymentMonthly
                      }}, followed by estimated monthly payments of ${{
                        quoteResponse.policies[0].findBilling.estimatedMonthlyPayment.toFixed(
                          2
                        )
                      }}. If you selected the Pay-Per-Mile billing plan for one
                      or more vehicles, your monthly payment will vary based on
                      how many miles those vehicles were driven during the
                      month.
                    </span>
                    <i class="icon icon--info tool-tip">
                      <div class="content">
                        <p
                          v-if="
                            this.quoteResponse.policies[0].quoteRequest
                              .ratedState !== 'TX'
                          "
                        >
                          We are committed to providing you with great service
                          and 100% transparent pricing. Your initial payment
                          includes a monthly Policy Fee of $5.00 and a monthly
                          Credit Card Fee of $4.95. Future monthly payments will
                          also include a Mileage Monitoring Fee of $5.00.
                        </p>
                        <p v-else>
                          We are committed to providing you with great service
                          and 100% transparent pricing. Your initial payment
                          includes a monthly Policy Fee of $5.00 and a monthly
                          Convenience Fee of $5.00. Future monthly
                          payments will also include a Mileage Monitoring Fee of
                          $5.00.
                        </p>
                        <i></i>
                      </div>
                    </i>
                  </span>
                </label>
                <div class="frequency-divider"></div>
                <label
                  class="input -spacing-static-c"
                  :key="radios.duration[1].value"
                  :class="{
                    radio: true,
                    error: errors.has('six-months')
                  }"
                >
                  <input
                    class="radio__field"
                    type="radio"
                    name="six-months"
                    :value="radios.duration[1].value"
                    v-model="form.selectedPricingScheme"
                    v-validate="'required'"
                  />
                  <span class="radio__icon icon icon--radio"></span>
                  <span class="radio__label">
                    I'd like to pay
                    <b>every six months</b>
                    <br />
                    <span class="sub-label-text">
                      Start your policy today for a single payment of ${{
                        roundedDownPaymentSixMonthly
                      }}, including estimated mileage charges for any vehicles
                      on the Pay-Per-Mile billing plan. At the end of the six
                      month policy term, you will receive a final bill or a
                      credit based on how many miles those vehicles were driven
                      during the policy term.
                    </span>
                    <i class="icon icon--info tool-tip">
                      <div class="content">
                        <p
                          v-if="
                            this.quoteResponse.policies[0].quoteRequest
                              .ratedState !== 'TX'
                          "
                        >
                          We are committed to providing you with great service
                          and 100% transparent pricing. Your one-time payment
                          includes a Policy Fee of $30.00 and a Credit Card Fee
                          of $4.95.
                        </p>
                        <p v-else>
                          We are committed to providing you with great service
                          and 100% transparent pricing. Your one-time payment
                          includes a Policy Fee of $30.00.
                        </p>
                        <i></i>
                      </div>
                    </i>
                  </span>
                </label>
              </div>
            </div>
            <div class="payment-section">
              <div class="header">
                <p>Your billing information*</p>
              </div>
              <div class="method-particulars credit-card">
                <label class="input -spacing-static-f">
                  <input
                    :class="{
                      input__field: true,
                      'force-placeholder': true,
                      error: errors.has('Name on Card')
                    }"
                    name="Name on Card"
                    placeholder="John Doe"
                    type="text"
                    v-model="form.nameOnCard"
                    v-validate="'required'"
                  />
                  <span class="input__label">Name on Card</span>
                </label>
                <label class="input -spacing-static-f">
                  <div
                    id="card-number"
                    name="'Card Number"
                    ref="cardNumberElement"
                    :class="{
                      input__field: true,
                      error: stripeErrors.includes('Card Number is Required')
                    }"
                  ></div>
                  <span class="input__label">Card Number</span>
                </label>
                <label class="input -spacing-static-f">
                  <div
                    id="card-expiry"
                    name="Expiry"
                    :class="{
                      input__field: true,
                      error: stripeErrors.includes('Expiry date is Required')
                    }"
                  ></div>
                  <span class="input__label">Expiration Date</span>
                </label>
                <label class="input -spacing-static-f">
                  <div
                    id="card-cvc"
                    name="CVC"
                    :class="{
                      input__field: true,
                      error: stripeErrors.includes('CVC is Required')
                    }"
                  ></div>
                  <span class="input__label">CVC</span>
                </label>
              </div>
              <div class="banner-section">
                <div class="banner-text">
                  *Only debit and credit cards accepted
                </div>
                <div class="banner">
                  <img
                    alt="Powered by Stripe"
                    :srcset="
                      require(`@/assets/img/powered_by_stripe@3x.png`) +
                        ' 3x, ' +
                        require(`@/assets/img/powered_by_stripe@2x.png`) +
                        ' 2x, ' +
                        require(`@/assets/img/powered_by_stripe.png`) +
                        ' 1x'
                    "
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="column is-half has-columns-above right-section">
            <div class="box -spacing-g -spacing-inner-c">
              <div class="header -spacing-static-c">
                <h3>Quote #{{ quoteResponse.policies[0].policyNumber }}</h3>
              </div>
              <div class="header-divider"></div>
              <div class="-spacing-static-c">
                <h4>Included in your Policy:</h4>
              </div>
              <div class="styled-list-box">
                <ul class="styled-list">
                  <li v-if="!unlimitedOnly">Pay-Per-Mile Rates</li>
                  <li>Porsche Genuine Parts</li>
                  <li>Claims Concierge</li>
                  <li>Non-invasive Data Collection</li>
                  <li>Agreed Value Coverage</li>
                  <li>Diminished Value Coverage</li>
                </ul>
              </div>
              <div class="header-divider"></div>
              <div class="premium-section">
                <div class="-spacing-static-c">
                  <h3 class="heading">Monthly Premium</h3>
                </div>
                <div
                  class="car-section"
                  v-for="(vehicle, index) in quoteResponse.policies[0]
                    .vehicleInfo"
                  :key="vehicle.carNumber"
                >
                  <h4>
                    {{
                      formatTextToTitleCase(vehicle.year) +
                        ' ' +
                        formatTextToTitleCase(vehicle.make) +
                        ' ' +
                        formatTextCapitalCase(vehicle.model)
                    }}
                  </h4>
                  <div class="columns is-gapless level flex--row-wrap">
                    <div class="column is-7 level-left">Base Charge:</div>
                    <div class="column is-5 level-right">
                      <span v-if="quoteResponse"
                        >${{
                          quoteResponse.policies[0].vehicleInfo[
                            index
                          ].premiumInfo.baseRate.toFixed(2)
                        }}</span
                      >
                      <span v-else>(N/A)</span>&nbsp;per month
                    </div>
                  </div>

                  <div
                    v-if="
                      quoteResponse.policies[0].quoteRequest.vehicles[index]
                        .billingType === 'per_mile'
                    "
                    class="columns is-mobile"
                  >
                    <div class="column is-7">Per Mile Charge:</div>
                    <div class="column is-5 level-right">
                      <span v-if="quoteResponse">
                        ${{
                          quoteResponse.policies[0].vehicleInfo[
                            index
                          ].premiumInfo.perMileRate.toFixed(3)
                        }}
                      </span>
                      <span v-else>-</span>&nbsp;per mile
                    </div>
                  </div>

                  <div
                    v-if="
                      quoteResponse.policies[0].quoteRequest.vehicles[index]
                        .billingType === 'per_mile'
                    "
                    class="columns is-mobile"
                  >
                    <div class="column is-7" style="padding-right:">
                      Your Estimated Driving (monthly):
                    </div>
                    <div
                      v-if="quoteResponse"
                      style="position: relative"
                      class="column is-5 level-right"
                    >
                      <span>
                        {{
                          quoteResponse.policies[0].vehicleInfo[index]
                            .premiumInfo.estMonthlyMiles
                        }}
                        miles
                      </span>
                    </div>
                    <div v-else class="column is-5 level-right">
                      <span>-</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="total-divider"></div>
              <div class="total-section">
                <h3 class="heading">
                  Total Policy Premium
                </h3>
                <div class="columns is-gapless level flex--row-wrap">
                  <div class="column is-8 level-left">
                    <h4>
                      Estimated Monthly Payment:
                      <i class="icon icon--info tool-tip">
                        <div class="content">
                          <p>
                            Your Estimated Monthly Payment is based on your
                            estimated miles driven for each vehicle on the
                            policy, and the amount of your Down Payment. It
                            includes applicable fees.
                            <strong
                              >Your actual monthly payment will change each
                              month based on the number of miles you
                              drive.</strong
                            >
                          </p>
                          <i></i>
                        </div>
                      </i>
                    </h4>
                  </div>
                  <div v-if="quoteResponse" class="column is-4 level-right">
                    <h4>
                      ${{
                        quoteResponse.policies[0].findBilling.estimatedMonthlyPayment.toFixed(
                          2
                        )
                      }}
                    </h4>
                  </div>
                  <div v-else class="column is-4">-</div>
                </div>
                <div class="columns is-gapless level flex--row-wrap">
                  <div class="column is-8 level-left">
                    <h4>
                      Estimated Six-Month Payment (includes fees):
                      <i class="icon icon--info tool-tip">
                        <div class="content">
                          <p
                            v-if="
                              this.quoteResponse.policies[0].quoteRequest
                                .ratedState !== 'TX'
                            "
                          >
                            We are committed to providing you with great service
                            and 100% transparent pricing. Your one-time payment
                            includes a monthly Policy Fee of $5.00 ($30.00
                            total) and a Credit Card Fee of $4.95.
                          </p>
                          <p v-else>
                            We are committed to providing you with great service
                            and 100% transparent pricing. Your one-time payment
                            includes a monthly Policy Fee of $5.00 ($30.00
                            total).
                          </p>
                          <i></i>
                        </div>
                      </i>
                    </h4>
                  </div>
                  <div class="column is-3 level-right">
                    <h4>${{ roundedDownPaymentSixMonthly }}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Lienholders
          v-if="showLienHolders"
          @sendDataBack="gotDataBackFromLienholders"
          :lienHoldersData="lienHoldersData"
          :selects="selects"
          :lienholderVehicles="lienHoldersVehicles"
        ></Lienholders>
        <div class="button-right-align">
          <button
            @click="backButton()"
            type="button"
            v-if="
              currentStepNumber > 1 &&
                currentStepNumber <= 6 &&
                !showLienHolders
            "
            class="button-primary button-primary--black -spacing-static-b back-button"
          >
            <span class="button-primary__icon icon icon--arrow-right-hair">
              <span class="loader-base button-primary__loader"></span>
            </span>

            <span class="button-primary__label">Back</span>
          </button>

          <button
            @click="submitPaymentForm()"
            type="button"
            class="button-primary button-primary--black -spacing-static-b payment-continue-button"
            v-if="currentStepNumber !== 7 && !showLienHolders"
            :disabled="paymentInProgress"
          >
            <span class="button-primary__icon icon icon--arrow-right-hair">
              <span class="loader-base button-primary__loader"></span>
            </span>

            <span class="button-primary__label">{{
              isLastStep ? 'Submit Payment' : 'Continue'
            }}</span>
          </button>
        </div>
      </fieldset>
    </form>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Lienholders from './Lienholders';
import commonModal from '../shared/commonModal';
import { mapMutations } from 'vuex';
import axios from 'axios';
import * as _ from 'lodash';

export default {
  props: {
    wizardData: {
      type: Object,
      required: true
    },
    currentStepNumber: {
      type: Number
    },
    isLastStep: {
      type: Boolean
    }
  },

  data: () => ({
    payHeading: 'Your Payment',
    paymentStepIndex: 0,
    lienHead: 'Your Lienholder',
    isActive: false,
    unlimitedOnly: false,
    showLienHolders: false,
    form: {
      selectedPricingScheme: 'monthly',
      nameOnCard: ''
    },
    paymentCompleteFlags: {
      number: '',
      expiry: '',
      cvc: ''
    },
    paymentInProgress: false,
    paymentFailedModal: false,
    paymentFailed: false,
    // contains all the lienholder data... TODO: move to store.
    lienHoldersData: [],
    lienHoldersVehicles: [],
    selects: {
      lienHolderOptions: [
        {
          label: 'Porsche Financial Services [Financed through Porsche]',
          value: 'PFS'
        },
        {
          label: ' Porsche Leasing Limited [Leased through Porsche]',
          value: 'PLL'
        },
        {
          label: 'Other',
          value: 'other'
        },
        {
          label: "I Don't Know",
          value: "I don't know"
        }
      ],
      lienHolderStates: [
        {
          label: 'Alabama',
          value: 'AL'
        },
        {
          label: 'Alaska',
          value: 'AK'
        },
        {
          label: 'Arizona',
          value: 'AZ'
        },
        {
          label: 'Arkansas',
          value: 'AR'
        },
        {
          label: 'California',
          value: 'CA'
        },
        {
          label: 'Colorado',
          value: 'CO'
        },
        {
          label: 'Connecticut',
          value: 'CT'
        },
        {
          label: 'Delaware',
          value: 'DE'
        },
        {
          label: 'District Of Columbia',
          value: 'DC'
        },
        {
          label: 'Florida',
          value: 'FL'
        },
        {
          label: 'Georgia',
          value: 'GA'
        },
        {
          label: 'Hawaii',
          value: 'HI'
        },
        {
          label: 'Idaho',
          value: 'ID'
        },
        {
          label: 'Illinois',
          value: 'IL'
        },
        {
          label: 'Indiana',
          value: 'IN'
        },
        {
          label: 'Iowa',
          value: 'IA'
        },
        {
          label: 'Kansas',
          value: 'KS'
        },
        {
          label: 'Kentucky',
          value: 'KY'
        },
        {
          label: 'Louisiana',
          value: 'LA'
        },
        {
          label: 'Maine',
          value: 'ME'
        },
        {
          label: 'Maryland',
          value: 'MD'
        },
        {
          label: 'Massachusetts',
          value: 'MA'
        },
        {
          label: 'Michigan',
          value: 'MI'
        },
        {
          label: 'Minnesota',
          value: 'MN'
        },
        {
          label: 'Mississippi',
          value: 'MS'
        },
        {
          label: 'Missouri',
          value: 'MO'
        },
        {
          label: 'Montana',
          value: 'MT'
        },
        {
          label: 'Nebraska',
          value: 'NE'
        },
        {
          label: 'Nevada',
          value: 'NV'
        },
        {
          label: 'New Hampshire',
          value: 'NH'
        },
        {
          label: 'New Jersey',
          value: 'NJ'
        },
        {
          label: 'New Mexico',
          value: 'NM'
        },
        {
          label: 'New York',
          value: 'NY'
        },
        {
          label: 'North Carolina',
          value: 'NC'
        },
        {
          label: 'North Dakota',
          value: 'ND'
        },
        {
          label: 'Ohio',
          value: 'OH'
        },
        {
          label: 'Oklahoma',
          value: 'OK'
        },
        {
          label: 'Oregon',
          value: 'OR'
        },
        {
          label: 'Pennsylvania',
          value: 'PA'
        },
        {
          label: 'Rhode Island',
          value: 'RI'
        },
        {
          label: 'South Carolina',
          value: 'SC'
        },
        {
          label: 'South Dakota',
          value: 'SD'
        },
        {
          label: 'Tennessee',
          value: 'TN'
        },
        {
          label: 'Texas',
          value: 'TX'
        },
        {
          label: 'Utah',
          value: 'UT'
        },
        {
          label: 'Vermont',
          value: 'VT'
        },
        {
          label: 'Virginia',
          value: 'VA'
        },
        {
          label: 'Washington',
          value: 'WA'
        },
        {
          label: 'West Virginia',
          value: 'WV'
        },
        {
          label: 'Wisconsin',
          value: 'WI'
        },
        {
          label: 'Wyoming',
          value: 'WY'
        }
      ]
    },
    // Elements
    paymentToken: null,
    cardNumber: '',
    cardExpiry: '',
    cardCvc: '',
    stripeKey: process.env.VUE_APP_STRIPE_KEY,
    stripe: null,
    elements: null,
    loading: false,
    stripeErrors: [],
    radios: {
      duration: [
        {
          value: 'monthly'
        },
        {
          value: 'biannually'
        }
      ]
    },
    showWarningModal: false
  }),

  components: {
    Lienholders,
    commonModal
  },
  beforeMount() {
    this.isActive = true;
  },
  mounted() {
    this.setUpStripe();
    this.floodlightPurchase()
    const vehicles = this.quoteResponse.policies[0].quoteRequest.vehicles;
    if (vehicles) {
      let unlCount = 0;
      vehicles.forEach(v => {
        if (v.billingType === 'capped') {
          unlCount++;
        }
      });
      if (unlCount === vehicles.length) {
        this.unlimitedOnly = true;
      }
    }
  },
  activated() {
    this.isActive = true;
  },
  deactivated() {
    this.isActive = false;
  },
  computed: {
    ...mapGetters(['quoteResponse', 'bindResponse']),
    sixMonthlyFees() {
      let sixMonthFees = 33;
      if (this.quoteResponse.policies[0].ratedState === 'TX') {
        sixMonthFees = 30;
      }
      return (this.isActive && sixMonthFees) || sixMonthFees;
    },
    monthlyFees() {
      let monthlyFees = 13;
      if (this.quoteResponse.policies[0].ratedState === 'TX') {
        monthlyFees = 10;
      }
      return (this.isActive && monthlyFees) || monthlyFees;
    },
    totalMonthlyCost() {
      if (this.quoteResponse) {
        let monthlyTotal = 0;
        for (
          let i = 0;
          i < this.quoteResponse.policies[0].vehicleInfo.length;
          i++
        ) {
          let vehicle = this.quoteResponse.policies[0].vehicleInfo[i];
          monthlyTotal += this.roundAmountTo(
            vehicle.premiumInfo.estMonthlyNoFee,
            2
          );
        }
        return (
          (this.isActive &&
            this.roundAmountTo(monthlyTotal + this.monthlyFees, 2)) ||
          this.roundAmountTo(monthlyTotal + this.monthlyFees, 2)
        );
      }
      return (this.isActive && 0.0) || 0.0;
    },
    sixMonthCost() {
      return (
        (this.isActive &&
          6.0 *
            this.roundAmountTo(this.totalMonthlyCost - this.monthlyFees, 2) +
            this.sixMonthlyFees) ||
        6.0 * this.roundAmountTo(this.totalMonthlyCost - this.monthlyFees, 2) +
          this.sixMonthlyFees
      );
    },
    roundedDownPaymentMonthly() {
      if (
        this.quoteResponse.policies[0] &&
        this.quoteResponse.policies[0].downPaymentMonthly
      ) {
        return (
          (this.isActive &&
            this.quoteResponse.policies[0].downPaymentMonthly.toFixed(2)) ||
          this.quoteResponse.policies[0].downPaymentMonthly.toFixed(2)
        );
      } else {
        return (
          (this.isActive && this.totalMonthlyCost.toFixed(2)) ||
          this.totalMonthlyCost.toFixed(2)
        );
      }
    },
    roundedDownPaymentSixMonthly() {
      if (this.quoteResponse.policies[0].downPaymentSingle) {
        return (
          (this.isActive &&
            this.quoteResponse.policies[0].downPaymentSingle.toFixed(2)) ||
          this.quoteResponse.policies[0].downPaymentSingle.toFixed(2)
        );
      } else {
        return (
          (this.isActive && this.sixMonthCost.toFixed(2)) ||
          this.sixMonthCost.toFixed(2)
        );
      }
    }
  },
  methods: {
    ...mapActions(['requestQuote', 'bindQuote', 'updateSingleLienholder']),
    ...mapMutations(['updateForm', 'updateLienHolderResponse', 'toggleLoader']),
    floodlightPurchase() {
      if (
          process.env.VUE_APP_ENV.toLowerCase() !== 'production' &&
          process.env.VUE_APP_ENV.toLowerCase() !== 'prod'
      ) {
        return;
      } else {
        // console.log('2025 Floodlight 5 Active');
        // Porsche PAI Receive a quote Form Payment Page Load
        let flDiv = document.body.appendChild(document.createElement('div'));
        let GDPR = '${GDPR}';
        let GDPR_CONSENT_755 = '${GDPR_CONSENT_755}';
        flDiv.setAttribute('id', process.env.VUE_APP_FLOODLIGHT_ID);
        flDiv.style.position = 'absolute';
        flDiv.style.top = '0';
        flDiv.style.left = '0';
        flDiv.style.width = '1px';
        flDiv.style.height = '1px';
        flDiv.style.display = 'none';
        flDiv.innerHTML = `
          <img src="https://ad.doubleclick.net/ddm/activity/src=5368208;type=2025p0;cat=porsc004;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;npa=;gdpr=${GDPR};gdpr_consent=${GDPR_CONSENT_755};ord=1?" width="1" height="1" alt=""/>
        `;
        gtag('event', 'conversion', {
          'allow_custom_scripts': true,
          'send_to': 'DC-5368208/2025p0/porsc004+standard'
        });
      }
    },
    backButton() {
      this.$emit('goBack');
    },
    getEstimatedMileageForIndividualVehicle(itemIndex) {
      if (this.quoteResponse && this.quoteResponse.policies[0].vehicleInfo) {
        let vehicle = this.quoteResponse.policies[0].vehicleInfo[itemIndex];
        return (
          vehicle.premiumInfo.perMileRate * vehicle.premiumInfo.estMonthlyMiles
        );
      }
    },
    setUpStripe() {
      if (window.Stripe === undefined) {
        alert('Stripe V3 library not loaded!');
      } else {
        const stripe = window.Stripe(this.stripeKey);
        this.stripe = stripe;
        const elements = stripe.elements();
        this.elements = elements;
        this.cardCvc = this.createElement('cardCvc', 'CVC *');
        this.cardExpiry = this.createElement('cardExpiry', 'MM / YY *');
        this.cardNumber = this.createElement(
          'cardNumber',
          '1234 1234 1234 1234 *'
        );
        this.cardCvc.mount('#card-cvc');
        this.cardExpiry.mount('#card-expiry');
        this.cardNumber.mount('#card-number');
        this.listenForErrors();
      }
    },
    scrollTop() {
      let container = document.getElementById('quote-container');
      setTimeout(() => {
        container.scrollIntoView(true);
      }, 100);
    },
    createElement(name, placeholder) {
      return this.elements.create(name, {
        placeholder: placeholder,
        style: {
          base: {
            fontFamily: 'PorscheNextRegular, "Arial Narrow", Arial, sans-serif',
            fontSize: '14px',
            color: '#000',
            '::placeholder': {
              color: '#dad9de'
            }
          },
          invalid: {
            color: '#d5001c'
          }
        },
        classes: {
          focus: 'focus',
          empty: 'force-placeholder',
          invalid: 'error'
        }
      });
    },
    listenForErrors() {
      this.cardNumber.addEventListener('change', event => {
        this.toggleError(event);
        this.paymentCompleteFlags.number = event.complete ? true : false;
      });
      this.cardExpiry.addEventListener('change', event => {
        this.toggleError(event);
        this.paymentCompleteFlags.expiry = event.complete ? true : false;
      });
      this.cardCvc.addEventListener('change', event => {
        this.toggleError(event);
        this.paymentCompleteFlags.cvc = event.complete ? true : false;
      });
    },
    toggleError(event) {
      if (event.error) {
        this.stripeErrors = this.concatWithStripeErrors(
          this.stripeErrors,
          event.error.message
        );
      } else {
        this.stripeErrors = [];
      }
    },
    concatWithStripeErrors(array, newError) {
      let newArray = array.concat(newError);
      return [...new Set(newArray)];
    },
    validateStripeFields() {
      let valid = true;
      if (!this.paymentCompleteFlags.number) {
        valid = false;
        this.stripeErrors = this.concatWithStripeErrors(
          this.stripeErrors,
          'Card Number is Required'
        );
      }
      if (!this.paymentCompleteFlags.cvc) {
        valid = false;
        this.stripeErrors = this.concatWithStripeErrors(
          this.stripeErrors,
          'CVC is Required'
        );
      }
      if (!this.paymentCompleteFlags.expiry) {
        valid = false;
        this.stripeErrors = this.concatWithStripeErrors(
          this.stripeErrors,
          'Expiry date is Required'
        );
      }
      return valid;
    },
    async submitFormToCreateToken() {
      if (this.validateStripeFields()) {
        this.clearCardErrors();
        return this.stripe.createToken(this.cardNumber);
      }
      return false;
    },
    async callQuoteToBind(data) {
      return this.bindQuote(data);
    },
    submitOneLienholderData(data) {
      return this.updateSingleLienholder(data);
    },
    clearCardErrors() {
      this.stripeErrors = [];
    },
    gotDataBackFromLienholders() {
      let payload = {
        lienHolderData: _.cloneDeep(this.lienHoldersData)
      };
      this.updateForm(payload);
      this.submitLienholderData();
    },
    submitLienholderData() {
      let multipleLienholderDataUpdations = [];
      let lienholderError = false;
      for (let i = 0; i < this.lienHoldersData.length; i++) {
        const thisLienholder = this.lienHoldersData[i];
        if (thisLienholder.skipSend) {
          continue;
        }
        let payload = {
          quoteNumber: this.bindResponse.policies[0].policyNumber,
          name: thisLienholder.name,
          address: thisLienholder.address,
          city: thisLienholder.city,
          state: thisLienholder.state.value,
          zip: thisLienholder.zip,
          phoneNumber: thisLienholder.phoneNumber,
          carNumber: thisLienholder.carNumber
        };
        multipleLienholderDataUpdations.push(
          this.submitOneLienholderData(payload).catch(() => {
            lienholderError = true;
          })
        );
      }
      this.showWarningModal = false;
      this.toggleLoader({
        loaderText: 'Loading...'
      });
      axios.all(multipleLienholderDataUpdations).then(
        axios.spread((...responses) => {
          this.toggleLoader();
          if (!lienholderError && responses) {
            for (let i = 0; i < responses.length; i++) {
              this.updateLienHolderResponse(responses[i]);
            }
            this.$emit('nextButtonAction');
          } else {
            this.showWarningModal = true;
          }
        })
      );
    },
    submitPaymentForm() {
      this.scrollTop();
      if (this.paymentFailed) {
        this.paymentFailedModal = true;
      } else {
        this.validateFields().then(result => {
          if (this.validateStripeFields() && result) {
            this.paymentInProgress = true;
            this.toggleLoader({
              loaderText:
                'Payment in progress. Please do not reload the page or go back.'
            });
            (async () => {
              const stripeResponse = await this.submitFormToCreateToken();
              if (!stripeResponse || stripeResponse.error) {
                if (this.stripeErrors.length) {
                  console.log(this.stripeErrors);
                }
                this.paymentInProgress = false;
                this.toggleLoader();
              } else {
                const token = stripeResponse.token.id;
                this.paymentToken = token;
                const payload = {
                  quoteNumber: this.quoteResponse.policies[0].policyNumber,
                  stripeKey: this.stripeKey,
                  paymentToken: this.paymentToken,
                  billingFrequency: this.form.selectedPricingScheme,
                  payPlan:
                    this.form.selectedPricingScheme === 'monthly' ? 18 : 21
                };
                await this.callQuoteToBind(payload)
                  .then(() => {
                    this.toggleLoader();
                    this.paymentInProgress = true;
                    this.checkForLienHolder();
                  })
                  .catch(err => {
                    const StripeErrors = [
                      'card_error',
                      'invalid_request_error'
                    ];

                    let message = `The system is unable to complete your request at this time.
                    Please call <span style="white-space: nowrap;"><a style="text-decoration:underline;color:#fff" href="1-800-400-4128">800-400-4128</a></span>
                    to speak to a customer service concierge.`;

                    this.paymentFailed = true;
                    this.paymentInProgress = false;
                    this.toggleLoader();
                    if (err.includes(StripeErrors[0])) {
                      message =
                        'Your payment method has been declined. Please check to make sure your card information is correct, or try a different payment method.';
                    } else if (err.includes(StripeErrors[1])) {
                      message =
                        'We were unable to process your payment - please check that your card information is correct and try again.';
                    }
                    this.$snackbar.open({
                      type: 'is-danger',
                      message: message,
                      position: 'is-bottom'
                    });
                  });
              }
            })();
          }
        });
      }
    },
    checkForLienHolder() {
      if (
        this.quoteResponse &&
        this.quoteResponse.policies[0] &&
        this.quoteResponse.policies[0].vehicleInfo &&
        this.quoteResponse.policies[0].vehicleInfo.length
      ) {
        this.quoteResponse.policies[0].vehicleInfo.forEach(vehicle => {
          if (
            vehicle.financing === 'LEASED' ||
            vehicle.financing === 'FINANCED'
          ) {
            this.showLienHolders = true;
            this.lienHoldersVehicles.push(vehicle);
            const carNumber = vehicle.carNumber;
            this.lienHoldersData.push({
              name: null,
              zip: null,
              address: null,
              city: null,
              state: {
                label: null,
                value: null
              },
              option: null,
              phoneNumber: null,
              carNumber: carNumber,
              skipSend: false,
              isDisabled: true,
              showStateDropdown: false
            });
          }
        });
      }
      if (!this.showLienHolders) {
        this.$emit('nextButtonAction');
      }
    },
    validateFields() {
      return new Promise((resolve, reject) => {
        this.$validator.validateAll().then(result => {
          if (result) {
            resolve(true);
          } else {
            reject('invalid fields');
          }
        });
      });
    },
    submit() {
      let firstErrorField;
      return new Promise((resolve, reject) => {
        this.$validator.validateAll().then(result => {
          if (result) {
            resolve(true);
          } else {
            firstErrorField = this.$el.querySelector(
              '[name="' + this.$validator.errors.items[0].field + '"]'
            );
            firstErrorField.focus();
            reject('invalid fields');
          }
        });
      });
    },
    clickedCloseOnModal(value) {
      this.showWarningModal = false;
      if (value === 1) {
        this.$emit('nextButtonAction');
      }
    },
    formatTextToTitleCase(textString) {
      return _.startCase(_.toLower(textString));
    },

    formatTextCapitalCase(textString) {
      return _.capitalize(textString);
    },
    roundAmountTo(number, digits) {
      if (digits === undefined) {
        digits = 0;
      }
      var multiplicator = Math.pow(10, digits);
      number = parseFloat((number * multiplicator).toFixed(11));
      return Math.round(number) / multiplicator;
    },
    isFloat(value) {
      if (value) {
        return Number(value) === value && value % 1 !== 0;
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.-spacing-inner-c {
  @media (min-width: 1200px) {
    padding: 2.5rem 1.5rem;
  }
}

.legend {
  font-family: PorscheNextThin, 'Arial Narrow', Arial, sans-serif;
  font-size: 1.5rem;
  font-weight: 100;
  line-height: 28px;
  margin: 0.5em 0;
}

.payment-lien-desc {
  font-size: 1rem;
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.38;
  letter-spacing: normal;
  text-align: left;
  color: #000000;
}
.columns:not(:last-child) {
  margin-bottom: 0;
}
.has-columns-below {
  padding-bottom: 0.25rem;
}
.has-columns-above {
  padding-top: 0.25rem;
}
.left-section {
  .header {
    p {
      margin-bottom: 0.85em;
    }
  }
  span.sub-label-text {
    line-height: 1rem;
    color: $jumbo;
  }
  .frequency-box {
    label span.radio__label {
      line-height: 1.6rem;
    }
    @media (min-width: 1200px) {
      padding-left: 2rem;
      label span.radio__label {
        padding-left: 1.25rem;
      }
    }
  }
  .frequency-divider {
    @extend .divider;
    margin: 2rem;
  }
  .payment-section {
    margin-top: 2.5rem;
    .method-particulars {
      label.input:first-child {
        margin-top: 0.9rem;
      }
      label.input:last-child {
        margin-bottom: 0.9rem;
      }
    }
    .banner-section {
      color: $jumbo;
      display: flex;
      justify-content: space-between;
    }
  }
}
.right-section {
  font-size: 0.8rem;
  h2,
  h3,
  h4 {
    font-family: $family-sans-serif-bold;
    text-transform: none;
    letter-spacing: 0;
    font-size: 1rem;
    color: $subtitle-color;
  }
  h3 {
    font-size: 0.9rem;
  }
  h4 {
    font-size: 0.8rem;
    margin-bottom: 0.6rem;
  }
  .premium-section {
    .car-section {
      margin-top: 1rem;
      margin-bottom: 0.5rem;
      &:last-child {
        padding-top: 0.5rem;
        margin-bottom: 1rem;
      }

      h4 {
        margin-bottom: 0;
      }
    }
  }
  .car-section .columns,
  .total-section .columns {
    align-items: flex-start;
    color: $subtitle-color;
    @media (max-width: 480px) {
      display: flex;
    }
  }
  .level-right {
    text-align: right;
  }
  .header-divider,
  .total-divider {
    @extend .divider;
    margin: 0.9rem 0;
  }
}
.divider {
  border-top: 1px solid #dad9de;
  padding-bottom: 0.25rem;
}
.radio {
  display: block;
  white-space: normal;
  & + .radio {
    margin-left: 0;
  }
}
ul {
  margin-top: 1.25rem;
  margin-left: 0px !important;
  @media (min-width: 1200px) {
    columns: 2;
  }
}

li {
  margin-bottom: 1rem;
  font-size: 1rem;
  color: #000000;
}

.tool-tip {
  position: absolute;
  margin-top: 3px;

  .content {
    box-sizing: border-box;
    color: #000;
    display: none;
    font-size: 14px;
    font-family: $family-sans-serif;
    line-height: 20px;
    padding: 18px;
    top: 40px;
    transform: translate(-50%, 0);
    background-color: $porcelain;
    position: absolute;
    z-index: 99999999;

    @media (max-width: 479px) {
      width: 50vw;
      left: 8px;
    }

    @media (min-width: 480px) {
      left: 50%;
      line-height: 1.5;
      font-size: 1rem;
      padding: 25px 30px;
      width: 415px;
    }

    i {
      position: absolute;
      bottom: 100%;
      left: 50%;
      margin-left: -20px;
      width: 40px;
      height: 20px;
      overflow: hidden;

      &::after {
        content: '';
        position: absolute;
        width: 20px;
        height: 20px;
        left: 50%;
        transform: translate(-50%, 50%) rotate(45deg);
        background-color: $porcelain;
      }
    }
  }
  &:hover .content {
    display: block;
  }
}
</style>
